import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiEdit2,
  FiPlus,
  FiRefreshCw,
  FiSave,
  FiTrash2,
  FiX,
  FiCheck,
  FiSearch,
  FiShield,
  FiMapPin,
  FiLayers,
  FiEye,
  FiCheckSquare,
  FiLock,
  FiGlobe,
  FiAlertTriangle,
  FiCheckCircle,
  FiStar,
  FiUser
} from 'react-icons/fi';
import { filterDataService, usersService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Loading from '../common/Loading';

const SECTIONS = [
  { key: 'dashboard', label: 'Dashboard', desc: 'Visão geral e gráficos consolidados' },
  { key: 'vendas', label: 'Vendas', desc: 'Consulta e auditoria de pedidos e cupons fiscais' },
  { key: 'relatorios', label: 'Relatórios', desc: 'Relatórios de faturamento e agendamentos' },
  { key: 'monitoramento', label: 'Monitoramento', desc: 'Painel operacional e status em tempo real' },
  { key: 'bi_metas', label: 'BI de Metas', desc: 'Performance comercial, PA e ticket médio' },
  { key: 'vendedores', label: 'Vendedores', desc: 'Cadastro e manutenção de vendedores' },
  { key: 'metas', label: 'Metas', desc: 'Definição e acompanhamento de metas' },
  { key: 'grupos_metas', label: 'Grupos de Metas', desc: 'Presets e agrupamentos dinâmicos de metas' },
  { key: 'filtros', label: 'Filtros', desc: 'Regras e filtros de relatórios' },
  { key: 'variaveis', label: 'Variáveis', desc: 'Variáveis de cálculo e fórmulas de faturamento' },
  { key: 'funcionarios', label: 'Funcionários Winthor', desc: 'Colaboradores sincronizados do ERP' },
  { key: 'modulos', label: 'Módulos', desc: 'Status de runners e serviços em segundo plano' },
  { key: 'ia', label: 'Inteligência IA', desc: 'Provedores de inteligência artificial e prompts' },
  { key: 'usuarios', label: 'Usuários', desc: 'Gestão de acessos, perfis e permissões' }
];

const emptyPermissions = () =>
  SECTIONS.reduce((acc, section) => {
    acc[section.key] = { can_view: false, can_manage: false };
    return acc;
  }, {});

function Usuarios() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchSearch, setBranchSearch] = useState('');
  const [form, setForm] = useState({
    id: null,
    name: '',
    email: '',
    password: '',
    role: 'viewer',
    active: 1,
    permissions: emptyPermissions(),
    allowed_branches: []
  });

  const canEditPermissions = useMemo(() => form.role !== 'admin', [form.role]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersService.getAll();
      setUsers(res?.data?.users || []);
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao carregar usuários' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const res = await filterDataService.getFiliais();
      setBranches(res?.data?.filiais || []);
    } catch (e) {
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadBranches();
  }, [loadUsers, loadBranches]);

  const resetForm = () => {
    setBranchSearch('');
    setForm({
      id: null,
      name: '',
      email: '',
      password: '',
      role: 'viewer',
      active: 1,
      permissions: emptyPermissions(),
      allowed_branches: []
    });
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (u) => {
    setBranchSearch('');
    setForm({
      id: u.id,
      name: u.name || '',
      email: u.email || '',
      password: '',
      role: u.role || 'viewer',
      active: Number(u.active ?? 1),
      permissions: { ...emptyPermissions(), ...(u.permissions || {}) },
      allowed_branches: Array.isArray(u.allowed_branches) ? u.allowed_branches.map(String) : []
    });
    setShowModal(true);
  };

  const updatePermission = (sectionKey, field, value) => {
    setForm((prev) => {
      const next = { ...prev.permissions };
      const section = { ...(next[sectionKey] || { can_view: false, can_manage: false }) };
      section[field] = value;
      if (field === 'can_manage' && value) {
        section.can_view = true;
      }
      if (field === 'can_view' && !value) {
        section.can_manage = false;
      }
      next[sectionKey] = section;
      return { ...prev, permissions: next };
    });
  };

  const setAllPermissions = (type) => {
    setForm((prev) => {
      const next = {};
      SECTIONS.forEach((s) => {
        if (type === 'all_manage') {
          next[s.key] = { can_view: true, can_manage: true };
        } else if (type === 'all_view') {
          next[s.key] = { can_view: true, can_manage: false };
        } else {
          next[s.key] = { can_view: false, can_manage: false };
        }
      });
      return { ...prev, permissions: next };
    });
  };

  const setAllBranches = (selectAll) => {
    if (selectAll) {
      const allCodes = branches.map((b) => String(b.codigo));
      setForm((prev) => ({ ...prev, allowed_branches: allCodes }));
    } else {
      setForm((prev) => ({ ...prev, allowed_branches: [] }));
    }
  };

  const filteredBranches = useMemo(() => {
    if (!branchSearch.trim()) return branches;
    const q = branchSearch.toLowerCase();
    return branches.filter((b) => {
      const code = String(b.codigo || '');
      const nome = String(b.nomefantasia || '').toLowerCase();
      const razao = String(b.razaosocial || '').toLowerCase();
      return code.includes(q) || nome.includes(q) || razao.includes(q);
    });
  }, [branches, branchSearch]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        active: Number(form.active),
        permissions: form.permissions,
        allowed_branches: form.role === 'admin' ? null : form.allowed_branches
      };
      if (form.password.trim()) {
        payload.password = form.password.trim();
      }

      if (form.id) {
        await usersService.update(form.id, payload);
        setMessage({ type: 'success', text: 'Usuário atualizado com sucesso.' });
      } else {
        if (!payload.password) {
          setMessage({ type: 'danger', text: 'A senha é obrigatória para novo usuário.' });
          setSaving(false);
          return;
        }
        await usersService.create(payload);
        setMessage({ type: 'success', text: 'Usuário criado com sucesso.' });
      }
      setShowModal(false);
      resetForm();
      await loadUsers();
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao salvar usuário' });
    } finally {
      setSaving(false);
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm('Deseja excluir este usuário?')) return;
    try {
      await usersService.remove(id);
      setMessage({ type: 'success', text: 'Usuário removido com sucesso.' });
      await loadUsers();
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao excluir usuário' });
    }
  };

  const branchesLookup = useMemo(() => {
    const map = {};
    branches.forEach((b) => {
      const code = String(b.codigo || '');
      if (code) {
        map[code] = b.nomefantasia || b.razaosocial || `Filial ${code}`;
      }
    });
    return map;
  }, [branches]);

  const renderRoleBadge = (u) => {
    if (u.role === 'admin') {
      return (
        <span className="badge-pill-modern badge-role-admin" title="Administrador Geral - Acesso total e irrestrito">
          <FiShield size={12} className="badge-ico" />
          <span>Administrador</span>
        </span>
      );
    }
    if (u.role === 'manager') {
      return (
        <span className="badge-pill-modern badge-role-manager" title="Gestor / Supervisor - Acessos configuráveis">
          <FiStar size={12} className="badge-ico" />
          <span>Gestor</span>
        </span>
      );
    }
    return (
      <span className="badge-pill-modern badge-role-viewer" title="Operador / Leitor - Acesso restrito a operações autorizadas">
        <FiUser size={12} className="badge-ico" />
        <span>Operador</span>
      </span>
    );
  };

  const renderBranchBadges = (u) => {
    if (u.role === 'admin') {
      return (
        <span className="badge-pill-modern badge-branch-admin" title="Administrador tem acesso irrestrito a todas as filiais">
          <FiGlobe size={12} className="badge-ico" />
          <span>Todas as Filiais</span>
        </span>
      );
    }

    const allowed = Array.isArray(u.allowed_branches) ? u.allowed_branches : [];
    if (allowed.length === 0) {
      return (
        <span className="badge-pill-modern badge-branch-none" title="Nenhuma filial liberada para este usuário">
          <FiAlertTriangle size={12} className="badge-ico" />
          <span>Sem Filial Vinculada</span>
        </span>
      );
    }

    const visibleBranches = allowed.slice(0, 3);
    const remainingCount = allowed.length - 3;
    const fullTooltip = allowed
      .map((code) => `#${code} - ${branchesLookup[String(code)] || 'Filial ' + code}`)
      .join('\n');

    return (
      <div className="d-flex flex-column gap-1">
        <div className="d-flex flex-wrap gap-1 align-items-center" title={fullTooltip}>
          {visibleBranches.map((code) => (
            <span
              key={code}
              className="badge-branch-pill"
              title={`#${code} - ${branchesLookup[String(code)] || 'Filial ' + code}`}
            >
              <span className="badge-dot-emerald" />
              <span>#{code}</span>
            </span>
          ))}
          {remainingCount > 0 && (
            <span className="badge-branch-pill badge-branch-more" title={fullTooltip}>
              +{remainingCount} mais
            </span>
          )}
        </div>
        <span className="text-muted" style={{ fontSize: '0.72rem', fontWeight: 500 }}>
          {allowed.length} {allowed.length === 1 ? 'filial liberada' : 'filiais liberadas'}
        </span>
      </div>
    );
  };

  const renderAccessBadges = (u) => {
    const isAdm = u.role === 'admin';
    if (isAdm) {
      return (
        <span className="badge-pill-modern badge-access-admin" title="Acesso completo automático a todas as seções e operações">
          <FiCheckCircle size={12} className="badge-ico" />
          <span>Acesso Total ({SECTIONS.length} seções)</span>
        </span>
      );
    }

    const permEntries = Object.entries(u.permissions || {});
    const viewSections = permEntries.filter(([k, p]) => p?.can_view).map(([k]) => {
      const s = SECTIONS.find((sec) => sec.key === k);
      return s ? s.label : k;
    });
    const manageSections = permEntries.filter(([k, p]) => p?.can_manage).map(([k]) => {
      const s = SECTIONS.find((sec) => sec.key === k);
      return s ? s.label : k;
    });

    const viewCount = viewSections.length;
    const manageCount = manageSections.length;

    const viewTooltip = viewCount > 0
      ? `Visualização liberada (${viewCount} seções):\n• ` + viewSections.join('\n• ')
      : 'Nenhuma seção com permissão de visualização';

    const manageTooltip = manageCount > 0
      ? `Gerenciamento liberado (${manageCount} seções):\n• ` + manageSections.join('\n• ')
      : 'Nenhuma seção com permissão de gerenciamento';

    return (
      <div className="d-flex flex-wrap gap-1 align-items-center">
        <span
          className="badge-pill-modern badge-access-view"
          title={viewTooltip}
          style={{ cursor: 'help' }}
        >
          <FiEye size={12} className="badge-ico" />
          <span>{viewCount} ver</span>
        </span>

        {manageCount > 0 ? (
          <span
            className="badge-pill-modern badge-access-manage"
            title={manageTooltip}
            style={{ cursor: 'help' }}
          >
            <FiCheckSquare size={12} className="badge-ico" />
            <span>{manageCount} gerenciar</span>
          </span>
        ) : (
          <span
            className="badge-pill-modern badge-empty-pill"
            title="Sem permissão de gerenciamento (apenas leitura)"
          >
            <FiLock size={10} className="badge-ico" />
            <span>Leitura</span>
          </span>
        )}
      </div>
    );
  };

  const renderStatusBadge = (u) => {
    const isActive = Number(u.active) === 1;
    if (isActive) {
      return (
        <span className="badge-pill-modern badge-status-active" title="Usuário ativo com permissão de login">
          <span className="badge-pulse-dot" />
          <span>Ativo</span>
        </span>
      );
    }
    return (
      <span className="badge-pill-modern badge-status-inactive" title="Usuário inativo - login bloqueado">
        <span className="badge-dot-gray" />
        <span>Inativo</span>
      </span>
    );
  };

  return (
    <div className="container-fluid py-4">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Usuários & Permissões</h2>
          <p className="text-muted mb-0">
            Gerencie o cadastro de usuários, perfil de segurança, escopo de filiais e controle granular por seção.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn-modern btn-modern-light" onClick={loadUsers} disabled={loading} title="Atualizar">
            <FiRefreshCw size={16} className={loading ? 'spin-ico' : ''} />
          </button>
          <button type="button" className="btn-modern btn-modern-primary" onClick={openCreate}>
            <FiPlus size={16} className="me-2" />Novo Usuário
          </button>
        </div>
      </div>

      {message && <div className={`alert alert-${message.type} py-2 mb-4`}>{message.text}</div>}

      <div className="card-modern">
        <div className="card-body-modern p-4" style={{ position: 'relative' }}>
          {loading && users.length > 0 && (
            <Loading.Overlay text="Atualizando usuários..." size="sm" />
          )}
          <div className="table-responsive">
            <table className="table-modern w-100">
              <thead>
                <tr>
                  <th>Nome & Email</th>
                  <th>Perfil</th>
                  <th>Escopo de Filiais</th>
                  <th>Acessos às Seções</th>
                  <th>Status</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && users.length === 0 ? (
                  <Loading.Table
                    colSpan={6}
                    text="Carregando usuários..."
                    subtext="Buscando permissões e perfis de acesso..."
                  />
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted">Nenhum usuário cadastrado.</td>
                  </tr>
                ) : (
                  users.map((u) => {
                    const isAdm = u.role === 'admin';
                    const allowedBr = u.allowed_branches;
                    const permEntries = Object.entries(u.permissions || {});
                    const viewCount = isAdm ? SECTIONS.length : permEntries.filter(([k, p]) => p?.can_view).length;
                    const manageCount = isAdm ? SECTIONS.length : permEntries.filter(([k, p]) => p?.can_manage).length;

                    return (
                      <tr key={u.id}>
                        <td>
                          <div className="fw-bold text-dark">{u.name}</div>
                          <div className="text-muted small">{u.email}</div>
                        </td>
                        <td>{renderRoleBadge(u)}</td>
                        <td>{renderBranchBadges(u)}</td>
                        <td>{renderAccessBadges(u)}</td>
                        <td>{renderStatusBadge(u)}</td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn-modern btn-modern-light btn-modern-sm me-2"
                            onClick={() => openEdit(u)}
                            title="Editar usuário e acessos"
                          >
                            <FiEdit2 size={14} />
                          </button>
                          {currentUser?.id !== u.id && (
                            <button
                              type="button"
                              className="btn-modern btn-modern-light-danger btn-modern-sm"
                              onClick={() => removeUser(u.id)}
                              title="Excluir usuário"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
        >
          <div
            style={{
              background: 'white', borderRadius: '20px', width: '100%',
              maxWidth: '820px', maxHeight: '92vh',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
              animation: 'modalSlideIn 0.25s ease',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 28px',
              borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'white', zIndex: 10, flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '42px', height: '42px', borderRadius: '12px',
                  background: 'var(--primary)', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FiShield size={20} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1.05rem' }}>
                    {form.id ? 'Editar Acessos & Dados do Usuário' : 'Novo Usuário do Sistema'}
                  </h5>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
                    Configure permissões granulares por seção e o escopo de filiais liberadas.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '8px' }}
                title="Fechar"
              >
                <FiX size={20} color="#64748b" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ padding: '22px 28px', overflowY: 'auto', flex: 1 }}>
                {/* Dados Básicos */}
                <div className="mb-3">
                  <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '0.9rem' }}>Dados do Usuário</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small text-muted mb-1">Nome Completo</label>
                      <input
                        className="form-control-modern"
                        placeholder="Ex: Maria Santos"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small text-muted mb-1">Email de Acesso</label>
                      <input
                        className="form-control-modern"
                        type="email"
                        placeholder="usuario@novotok.com.br"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Perfil / Papel</label>
                      <select
                        className="form-control-modern"
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                      >
                        <option value="viewer">viewer (Leitor / Operador)</option>
                        <option value="manager">manager (Gestor / Supervisor)</option>
                        <option value="admin">admin (Administrador Geral)</option>
                      </select>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label small text-muted mb-1">
                        {form.id ? 'Nova Senha (deixe em branco para manter)' : 'Senha de Acesso'}
                      </label>
                      <input
                        className="form-control-modern"
                        type="password"
                        placeholder={form.id ? 'Manter senha atual' : 'Defina a senha'}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                      />
                    </div>
                    <div className="col-md-3 d-flex align-items-end pb-1">
                      <label className="d-flex align-items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          style={{ width: '18px', height: '18px' }}
                          checked={Number(form.active) === 1}
                          onChange={(e) => setForm({ ...form, active: e.target.checked ? 1 : 0 })}
                        />
                        <span className="fw-semibold small">Usuário Ativo</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Seção de Permissões */}
                <div className="mt-4 pt-3 border-top">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <FiLayers className="text-primary" />
                      <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '0.95rem' }}>
                        Permissões por Seção do Sistema ({SECTIONS.length} seções)
                      </h6>
                    </div>
                    {form.role === 'admin' ? (
                      <span className="badge bg-success text-white px-3 py-1">
                        Admin possui acesso total automático
                      </span>
                    ) : (
                      <div className="d-flex gap-1">
                        <button
                          type="button"
                          className="btn-modern btn-modern-light btn-modern-sm py-1 px-2"
                          style={{ fontSize: '0.78rem' }}
                          onClick={() => setAllPermissions('all_view')}
                        >
                          Marcar Todos Ver
                        </button>
                        <button
                          type="button"
                          className="btn-modern btn-modern-light btn-modern-sm py-1 px-2"
                          style={{ fontSize: '0.78rem' }}
                          onClick={() => setAllPermissions('all_manage')}
                        >
                          Marcar Todos Gerenciar
                        </button>
                        <button
                          type="button"
                          className="btn-modern btn-modern-light-danger btn-modern-sm py-1 px-2"
                          style={{ fontSize: '0.78rem' }}
                          onClick={() => setAllPermissions('clear')}
                        >
                          Limpar Tudo
                        </button>
                      </div>
                    )}
                  </div>

                  {form.role === 'admin' ? (
                    <div className="alert alert-info py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-info-circle me-2"></i>
                      Administradores têm acesso irrestrito de Visualização e Gerenciamento em todos os módulos e seções.
                    </div>
                  ) : (
                    <p className="text-muted small mb-2">
                      <strong>Visualizar:</strong> permite entrar na tela e consultar registros da filial selecionada.<br />
                      <strong>Gerenciar:</strong> libera botões de Novo, Editar, Salvar, Excluir, Reprocessar e Cadastros.
                    </p>
                  )}

                  <div className="table-responsive border rounded" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                    <table className="table table-hover table-sm mb-0" style={{ fontSize: '0.86rem' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2 }}>
                        <tr>
                          <th style={{ width: '50%', fontWeight: 600, color: '#1e293b', padding: '10px 14px' }}>Seção / Módulo</th>
                          <th style={{ width: '25%', textAlign: 'center', fontWeight: 600, color: '#1e293b', padding: '10px 14px' }}>Visualizar</th>
                          <th style={{ width: '25%', textAlign: 'center', fontWeight: 600, color: '#1e293b', padding: '10px 14px' }}>Gerenciar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {SECTIONS.map((section) => (
                          <tr
                            key={section.key}
                            style={{
                              backgroundColor: canEditPermissions ? 'transparent' : '#f8fafc',
                              opacity: canEditPermissions ? 1 : 0.75
                            }}
                          >
                            <td style={{ padding: '8px 14px', verticalAlign: 'middle' }}>
                              <div className="fw-semibold text-dark">{section.label}</div>
                              <div className="text-muted" style={{ fontSize: '0.74rem' }}>{section.desc}</div>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '8px 14px' }}>
                              <input
                                type="checkbox"
                                disabled={!canEditPermissions}
                                checked={!canEditPermissions || !!form.permissions?.[section.key]?.can_view}
                                onChange={(e) => updatePermission(section.key, 'can_view', e.target.checked)}
                                style={{ width: '17px', height: '17px', cursor: canEditPermissions ? 'pointer' : 'default' }}
                              />
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '8px 14px' }}>
                              <input
                                type="checkbox"
                                disabled={!canEditPermissions}
                                checked={!canEditPermissions || !!form.permissions?.[section.key]?.can_manage}
                                onChange={(e) => updatePermission(section.key, 'can_manage', e.target.checked)}
                                style={{ width: '17px', height: '17px', cursor: canEditPermissions ? 'pointer' : 'default' }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Escopo de Filiais */}
                <div className="mt-4 pt-3 border-top">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <FiMapPin className="text-primary" />
                      <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '0.95rem' }}>
                        Escopo de Filiais Autorizadas
                      </h6>
                    </div>
                    {form.role === 'admin' ? (
                      <span className="badge bg-success text-white px-3 py-1">
                        Admin acessa todas as filiais
                      </span>
                    ) : (
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: '0.78rem' }}>
                          {form.allowed_branches.length} de {branches.length} filiais
                        </span>
                        <button
                          type="button"
                          className="btn-modern btn-modern-light btn-modern-sm py-1 px-2"
                          style={{ fontSize: '0.78rem' }}
                          disabled={form.role === 'admin'}
                          onClick={() => setAllBranches(true)}
                        >
                          Selecionar Todas
                        </button>
                        <button
                          type="button"
                          className="btn-modern btn-modern-light-danger btn-modern-sm py-1 px-2"
                          style={{ fontSize: '0.78rem' }}
                          disabled={form.role === 'admin'}
                          onClick={() => setAllBranches(false)}
                        >
                          Limpar
                        </button>
                      </div>
                    )}
                  </div>

                  {form.role !== 'admin' && (
                    <>
                      <div className="position-relative mb-2">
                        <input
                          type="text"
                          className="form-control-modern py-1 ps-4"
                          style={{ fontSize: '0.85rem' }}
                          placeholder="Pesquisar filial por código ou nome..."
                          value={branchSearch}
                          onChange={(e) => setBranchSearch(e.target.value)}
                        />
                        <FiSearch size={14} className="position-absolute" style={{ left: '12px', top: '10px', color: '#94a3b8' }} />
                      </div>
                      <small className="text-muted d-block mb-2">
                        O usuário poderá <strong>apenas consultar e gerenciar dados</strong> vinculados às filiais selecionadas abaixo.
                      </small>
                    </>
                  )}

                  <div className="border rounded p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    {filteredBranches.map((branch) => {
                      const branchCode = String(branch.codigo);
                      const selected = form.role === 'admin' || form.allowed_branches.includes(branchCode);
                      return (
                        <label
                          key={branchCode}
                          className="d-flex align-items-center justify-content-between p-2 rounded hover-bg-light cursor-pointer mb-1"
                          style={{ background: selected ? '#f0fdf4' : 'transparent', border: selected ? '1px solid #bbf7d0' : '1px solid transparent' }}
                        >
                          <span className="small">
                            <strong>#{branch.codigo}</strong> — {branch.nomefantasia || branch.razaosocial || 'Sem nome'}
                          </span>
                          <input
                            type="checkbox"
                            disabled={form.role === 'admin'}
                            checked={selected}
                            style={{ width: '16px', height: '16px' }}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setForm((prev) => {
                                const next = new Set(prev.allowed_branches);
                                if (checked) next.add(branchCode);
                                else next.delete(branchCode);
                                return { ...prev, allowed_branches: Array.from(next) };
                              });
                            }}
                          />
                        </label>
                      );
                    })}
                    {filteredBranches.length === 0 && (
                      <div className="text-muted small text-center py-2">
                        Nenhuma filial encontrada para a busca "{branchSearch}".
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '16px 28px 20px',
                display: 'flex', justifyContent: 'flex-end', gap: '12px',
                borderTop: '1px solid #f1f5f9',
                background: '#fafafa', zIndex: 10, flexShrink: 0
              }}>
                <button
                  type="button"
                  className="btn-modern btn-modern-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-modern btn-modern-primary"
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : <><FiSave size={16} className="me-1" />Salvar Usuário & Acessos</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`
        .spin-ico { animation: spin-ico 0.8s linear infinite; display: inline-block; }
        @keyframes spin-ico { to { transform: rotate(360deg); } }
        @keyframes modalSlideIn { from { opacity: 0; transform: translateY(-16px); } to { opacity: 1; transform: translateY(0); } }
        .hover-bg-light:hover { background-color: #f8fafc; }

        /* Modern Badges System */
        .badge-pill-modern {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.76rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          white-space: nowrap;
          transition: all 0.15s ease-in-out;
          user-select: none;
        }
        .badge-pill-modern:hover {
          transform: translateY(-1px);
        }
        .badge-ico {
          flex-shrink: 0;
        }

        /* Role Badges */
        .badge-role-admin {
          background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
          color: #9d174d;
          border: 1px solid #fbcfe8;
          box-shadow: 0 1px 2px rgba(157, 23, 77, 0.08);
        }
        .badge-role-manager {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          box-shadow: 0 1px 2px rgba(29, 78, 216, 0.08);
        }
        .badge-role-viewer {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          color: #475569;
          border: 1px solid #cbd5e1;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }

        /* Branch Badges */
        .badge-branch-admin {
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          color: #065f46;
          border: 1px solid #a7f3d0;
          box-shadow: 0 1px 2px rgba(6, 95, 70, 0.08);
        }
        .badge-branch-none {
          background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
          color: #be123c;
          border: 1px solid #fecdd3;
        }
        .badge-branch-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 7px;
          border-radius: 8px;
          font-size: 0.72rem;
          font-weight: 600;
          background: #f0fdf4;
          color: #15803d;
          border: 1px solid #bbf7d0;
          box-shadow: 0 1px 2px rgba(22, 101, 52, 0.04);
          transition: transform 0.12s ease;
        }
        .badge-branch-pill:hover {
          transform: translateY(-1px);
        }
        .badge-branch-more {
          background: #f8fafc;
          color: #64748b;
          border: 1px solid #cbd5e1;
        }
        .badge-dot-emerald {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background-color: #22c55e;
          flex-shrink: 0;
        }

        /* Access Badges */
        .badge-access-admin {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #166534;
          border: 1px solid #86efac;
          box-shadow: 0 1px 2px rgba(22, 101, 52, 0.08);
        }
        .badge-access-view {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          color: #0369a1;
          border: 1px solid #bae6fd;
          box-shadow: 0 1px 2px rgba(3, 105, 161, 0.08);
        }
        .badge-access-manage {
          background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
          color: #7e22ce;
          border: 1px solid #e9d5ff;
          box-shadow: 0 1px 2px rgba(126, 34, 206, 0.08);
        }
        .badge-empty-pill {
          background: #f8fafc;
          color: #94a3b8;
          border: 1px solid #e2e8f0;
          font-weight: 500;
          font-size: 0.72rem;
          padding: 3px 8px;
        }

        /* Status Badges */
        .badge-status-active {
          background: #f0fdf4;
          color: #15803d;
          border: 1px solid #bbf7d0;
          box-shadow: 0 1px 2px rgba(22, 101, 52, 0.06);
        }
        .badge-status-inactive {
          background: #f8fafc;
          color: #64748b;
          border: 1px solid #cbd5e1;
        }
        .badge-pulse-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background-color: #22c55e;
          box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.25);
          display: inline-block;
          flex-shrink: 0;
          animation: pulse-green 2s infinite;
        }
        @keyframes pulse-green {
          0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
          70% { box-shadow: 0 0 0 4px rgba(34, 197, 94, 0); }
          100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
        }
        .badge-dot-gray {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background-color: #94a3b8;
          display: inline-block;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
}

export default Usuarios;
